import { LazyLoader, Toast } from '@bp/ui-components';
import classNames from 'classnames';
import { PushNotificationWrapper } from 'components/PushNotificationWrapper';
import { Suspense, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { Browsers, getBrowser } from 'utils/browsers';
import styles from './App.module.scss';
import { SidebarWrapper } from './components/SidebarWrapper/SidebarWrapper';
import { DesktopContext } from './context/IsDesktop';
import { OrganizationConfigContextProvider } from './context/OrganizationConfigContext';
import { graphql } from './client/gql';
import { useQuery } from 'urql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { Header } from 'components/Header/Header';
import dayjs from 'dayjs';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { MobileMenu } from 'components/MobileMenu/MobileMenu';
import { useRefetchTrigger } from './hooks/matrix/useRefetchTrigger';

const DataLoaderDataDocument = graphql(`
    query DataLoaderData(
        $schoolYearWhere: SchoolYearWhere!
        $notificationSettingsWhere: NotificationSettingsWhere!
        $profile: ID!
        $calendarEventWhere: CalendarEventWhere!
        $dayConnection: String!
        $organizationUuid: ID!
    ) {
        ...OrganizationConfigFields
        ...PermissionCheckerFields
        ...HeaderQueryFields
        ...MobileMenuQueryFields
        ...PushNotificationWrapperFields
    }
`);

export function DataLoader() {
  const { pimAuthClaims } = useAuthClaims();
  const userUuid = pimAuthClaims.getUser().uuid;
  const profile = pimAuthClaims.getProfile();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const [{ data }, refetch] = useQuery({
    query: DataLoaderDataDocument,
    variables: {
      organizationUuid,
      dayConnection: dayjs().format(CONNECT_DATE_TEMPLATE),
      profileWhere: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
      schoolYearWhere: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
      notificationSettingsWhere: {
        userUuid,
      },
      profile: profile?.uuid ?? '',
      calendarEventWhere: {
        AND: [
          {
            OR: [
              {
                virtualLocations_SOME: {
                  running: true,
                },
              },
              {
                daysConnection_SOME: { node: { date: dayjs().format(CONNECT_DATE_TEMPLATE) } },
              },
            ],
          },
          {
            OR: [
              {
                holderConnection: {
                  Organization: {
                    node: { uuid: pimAuthClaims.getOrganization().uuid },
                  },
                },
              },
              {
                attendees_SOME: {
                  uuid: profile.uuid,
                },
              },
              {
                owner: {
                  uuid: profile.uuid,
                },
              },
            ],
          },
        ],
      },

    },
  });

  const browser = getBrowser();
  const appClasses = classNames(styles['app-content'], {
    [styles.chrome]: browser === Browsers.Chrome,
    [styles.safari]: browser === Browsers.Safari,
  });

  const { isDesktop } = useContext(DesktopContext);
  useRefetchTrigger({ refetch });

  return (
    <OrganizationConfigContextProvider query={data}>
      <SidebarWrapper query={data}>
        <PushNotificationWrapper query={data} onChange={refetch}>
          <div className={appClasses} data-cy='layoutWrapper'>
            <Header className={styles.header} query={data} />
            <main className={classNames(styles['app-container'], 'has-scrollbar')} data-cy='layoutContentColumn'>
              <Suspense fallback={<LazyLoader embedded transparent />}>
                <Outlet />
              </Suspense>
            </main>
            {!isDesktop && <MobileMenu className={styles['mobile-menu']} query={data} />}
          </div>
          <Toast />
        </PushNotificationWrapper>
      </SidebarWrapper>
    </OrganizationConfigContextProvider>
  );
}
